import { X, Check, AlertCircle, Trash2, Save } from "lucide-react";
import { Button } from "../ui/button";
import { LinksConfigSection, MetricsConfigSection, GalleryConfigSection } from "./ProjectsSubSections";
import { ProjectDetailsForm, type ProjectItem } from "./ProjectDetailsForm";
import { VersionDiffViewer } from "./VersionDiffViewer";

interface ProjectEditorProps {
  editingProject: Partial<ProjectItem>;
  setEditingProject: React.Dispatch<React.SetStateAction<Partial<ProjectItem> | null>>;
  onSave: () => void;
  onCancel: () => void;
  uploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  getParsedList: (jsonStr: string | undefined) => any[];
  activeDraft?: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  liveProject?: any;
}

export function ProjectEditor({
  editingProject,
  setEditingProject,
  onSave,
  onCancel,
  uploading,
  fileInputRef,
  handleImageUpload,
  getParsedList,
  activeDraft,
  onSaveDraft,
  onDiscardDraft,
  liveProject
}: ProjectEditorProps) {
  const updateField = (fields: Partial<ProjectItem>) => {
    setEditingProject((p) => ({ ...p!, ...fields }));
  };

  const images = getParsedList(editingProject.images);
  const links = getParsedList(editingProject.links);
  const metrics = getParsedList(editingProject.metrics);

  const diffFields = [
    { key: "title_en", label: "English Title" },
    { key: "title_tr", label: "Turkish Title" },
    { key: "summary_en", label: "English Summary", isLongText: true },
    { key: "summary_tr", label: "Turkish Summary", isLongText: true },
    { key: "detailed_content_en", label: "English Content", isLongText: true },
    { key: "detailed_content_tr", label: "Turkish Content", isLongText: true },
    { key: "version", label: "Version Tag" },
    { key: "tech_stack", label: "Tech Stack" },
  ];

  return (
    <div className="space-y-6 max-w-3xl p-5 border border-border/60 rounded-2xl bg-card/65 animate-fade-in text-left">
      <div className="flex justify-between items-center pb-2 border-b border-border/80">
        <h5 className="font-bold text-base text-brand-primary">{editingProject.title_en ? "Edit Project" : "Add Project"}</h5>
      </div>

      {/* Staging Actions Panel */}
      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {activeDraft && onDiscardDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          {onSaveDraft && (
            <Button
              type="button"
              size="sm"
              onClick={onSaveDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
            </Button>
          )}
        </div>
      </div>

      <ProjectDetailsForm project={editingProject} updateField={updateField} getParsedList={getParsedList} />

      <LinksConfigSection links={links} onChange={(updatedLinks) => updateField({ links: JSON.stringify(updatedLinks) })} />

      <MetricsConfigSection metrics={metrics} onChange={(updatedMetrics) => updateField({ metrics: JSON.stringify(updatedMetrics) })} />

      <GalleryConfigSection
        images={images}
        onChange={(updatedImages) => updateField({ images: JSON.stringify(updatedImages) })}
        uploading={uploading}
        onUploadClick={() => fileInputRef.current?.click()}
        fileInputRef={fileInputRef}
        handleImageUpload={handleImageUpload}
      />

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" /> Cancel
        </Button>
        <Button onClick={onSave} className="bg-brand-primary text-white">
          <Check className="h-4 w-4 mr-2" /> Publish Live
        </Button>
      </div>

      {liveProject && (
        <VersionDiffViewer
          live={liveProject}
          draft={editingProject}
          fieldsToCompare={diffFields}
        />
      )}
    </div>
  );
}
